// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useExecutedCollateralTransferV2 } from '@hooks/queries/collateralv2/collateral/useExecutedCollateralTransferV2';
import { useQueryCollateralAgreementSettlementObligations } from '@hooks/queries/collateralv2/collateral/useQueryCollateralAgreementSettlementObligations';
import { useQuerySettlementInstructions } from '@hooks/queries/collateralv2/collateral/useQuerySettlementInstructions';
import { createInstrumentKey } from '../../../utils/instrument';
import { InstructedRow } from './types';
import { useObligationsAllocations } from './useObligationsAllocations';

export const useGetCollateralRows = () => {
  const collateralAgreementSettlementObligations =
    useQueryCollateralAgreementSettlementObligations();

  const pendingObligations: InstructedRow[] = (
    collateralAgreementSettlementObligations?.data || []
  ).map((i) => {
    return {
      contractId: i.contractId,
      payload: {
        agreementId: i.payload.id,
        agreementCId: i.payload.collateralAgreementCid,
        partyA: i.payload.sender,
        partyB: i.payload.receiver,
        id: i.payload.id,
        positions: [],
        createdAt: i.payload.createdAt,
        allocateBefore: i.payload.allocateBefore,
        settleBefore: i.payload.settleBefore,
        instructId: i.payload.id,
      },
      state: 'Pending',
    };
  });

  const settlementInstructions = useQuerySettlementInstructions();
  const { getTransfersSum } = useObligationsAllocations();

  const instructedRows: InstructedRow[] = (settlementInstructions?.data || []).map((i) => {
    const transfersSum = getTransfersSum(
      i.payload.partyA,
      i.payload.partyB,
      i.payload.agreementId,
      i.payload.id,
    );

    const isAllocated = i.payload.instructedPositions.every((position) => {
      const instrumentKey = createInstrumentKey(position.instrument.id, position.instrument.admin);
      return (transfersSum[instrumentKey] ?? 0) >= parseFloat(position.amount);
    });

    return {
      contractId: i.contractId,
      payload: {
        agreementId: i.payload.agreementId,
        partyA: i.payload.partyA,
        partyB: i.payload.partyB,
        id: i.payload.id,
        positions: i.payload.instructedPositions,
        createdAt: i.createdAt,
        allocateBefore: i.payload.allocateBefore,
        settleBefore: i.payload.settleBefore,
        instructId: i.payload.id,
      },
      state: isAllocated ? 'Allocated' : 'Instructed',
    };
  });

  const executedTransfer = useExecutedCollateralTransferV2();

  const settledRows: InstructedRow[] = (executedTransfer?.data || []).map((i) => ({
    contractId: i.contractId,
    payload: {
      agreementId: i.payload.agreementId,
      partyA: i.payload.partyA,
      partyB: i.payload.partyB,
      id: i.payload.id,
      positions: i.payload.settledPositions,
      createdAt: i.createdAt,
      instructId: i.payload.id,
    },
    state: 'Settled',
  }));

  const sortedRows = [...pendingObligations, ...instructedRows, ...settledRows].sort(
    (a, b) => new Date(b.payload.createdAt).getTime() - new Date(a.payload.createdAt).getTime(),
  );

  return { rows: sortedRows };
};
